import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import FilmCard from '@/components/feature/FilmCard';
import { films } from '@/mocks/films';

export default function DetailPage() {
  const { id } = useParams();
  const film = useMemo(() => films.find((f) => f.id === id), [id]);

  useEffect(() => {
    document.title = film
      ? `${film.title} - ${film.type === 'movie' ? '电影' : '电视剧'}剧情介绍与演员资料`
      : '影视作品不存在 - 姐姐的朋友影视内容站';
  }, [film]);

  const related = useMemo(() => {
    if (!film) return [];
    return films
      .filter((f) => f.id !== film.id && (f.genres.some((g) => film.genres.includes(g)) || f.region === film.region))
      .sort((a, b) => b.heat - a.heat)
      .slice(0, 4);
  }, [film]);

  if (!film) {
    return (
      <div className="pt-16 md:pt-[72px]">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-24 flex flex-col items-center text-center">
          <span className="flex items-center justify-center w-16 h-16 rounded-full bg-background-100 text-foreground-400 mb-4">
            <i className="ri-file-unknow-line text-3xl" />
          </span>
          <h1 className="font-heading font-bold text-xl text-foreground-900 mb-2">
            影视作品不存在或已删除
          </h1>
          <p className="text-sm text-foreground-500 mb-6">该作品可能已被移除，返回首页浏览其他内容</p>
          <Link
            to="/"
            className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line" />
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 md:pt-[72px]">
      <div className="relative w-full h-[320px] md:h-[440px] overflow-hidden bg-primary-950">
        <img
          src={film.backdrop}
          alt={`${film.title} 剧照`}
          title={`${film.title} 剧情介绍`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950/95 via-primary-950/60 to-primary-950/30" />
      </div>

      <div className="max-w-[1280px] mx-auto px-4 md:px-8 -mt-40 md:-mt-56 relative z-10">
        <Breadcrumb
          items={[{ label: '首页', to: '/' }, { label: '影视分类', to: '/category' }, { label: film.title }]}
        />
      </div>

      <div className="max-w-[1280px] mx-auto px-4 md:px-8 mt-4 md:mt-6">
        <div className="flex flex-col md:flex-row gap-6 md:gap-10">
          <div className="w-40 md:w-56 shrink-0 mx-auto md:mx-0">
            <div className="w-full aspect-[2/3] rounded-lg overflow-hidden border border-background-200 bg-background-200">
              <img
                src={film.cover}
                alt={`${film.title} 海报`}
                title={`${film.title} 剧情介绍与演员资料`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="font-heading font-black text-2xl md:text-4xl text-foreground-950 mb-3">
              {film.title}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 italic mb-5">「{film.tagline}」</p>

            <div className="flex flex-wrap items-center gap-2 mb-5">
              <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-primary-100 text-primary-700">
                {film.type === 'movie' ? '电影' : '电视剧'}
              </span>
              {film.genres.map((g) => (
                <span key={g} className="px-2.5 py-1 rounded-md text-xs bg-secondary-100 text-secondary-900">
                  {g}
                </span>
              ))}
            </div>

            <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 text-sm mb-5">
              <div className="flex gap-2">
                <dt className="text-foreground-400 whitespace-nowrap">地区</dt>
                <dd className="text-foreground-800">{film.region}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-foreground-400 whitespace-nowrap">上映</dt>
                <dd className="text-foreground-800">{film.year}年</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-foreground-400 whitespace-nowrap">片长</dt>
                <dd className="text-foreground-800">{film.duration}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-foreground-400 whitespace-nowrap">导演</dt>
                <dd className="text-foreground-800">{film.director}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-foreground-400 whitespace-nowrap">主演</dt>
                <dd className="text-foreground-800 line-clamp-1">{film.cast.map((c) => c.name).join(' / ')}</dd>
              </div>
              {film.rating > 0 && (
                <div className="flex items-center gap-2">
                  <dt className="text-foreground-400 whitespace-nowrap">评分</dt>
                  <dd className="flex items-center gap-1 font-semibold text-accent-600">
                    <i className="ri-star-fill text-sm" />
                    {film.rating.toFixed(1)}
                  </dd>
                </div>
              )}
            </dl>

            {film.topicIds.length > 0 && (
              <Link
                to={`/topic/${film.topicIds[0]}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-article-line" />
                查看相关资讯专题
              </Link>
            )}
          </div>
        </div>

        <section className="mt-12 md:mt-16">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 flex items-center gap-3 mb-4">
            <span className="w-1.5 h-6 rounded-full bg-accent-500" />
            剧情简介
          </h2>
          <p className="text-sm md:text-base text-foreground-700 leading-loose max-w-4xl">{film.synopsis}</p>
        </section>

        <section className="mt-10">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 flex items-center gap-3 mb-4">
            <span className="w-1.5 h-6 rounded-full bg-accent-500" />
            影评摘要
          </h2>
          <blockquote className="rounded-lg bg-background-100 border-l-4 border-accent-500 p-5 md:p-6 max-w-4xl">
            <p className="text-sm md:text-base text-foreground-700 leading-loose">{film.review}</p>
            <footer className="text-xs text-foreground-400 mt-3">— 姐姐的朋友影视内容站 编辑部</footer>
          </blockquote>
        </section>

        <section className="mt-12">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 flex items-center gap-3 mb-6">
            <span className="w-1.5 h-6 rounded-full bg-accent-500" />
            演员阵容
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {film.cast.map((member) => (
              <Link
                key={member.actorId + member.role}
                to={`/actor/${member.actorId}`}
                className="group flex items-center gap-4 p-3 rounded-lg bg-background-100 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
              >
                <div className="w-14 h-14 md:w-16 md:h-16 shrink-0 rounded-full overflow-hidden bg-background-200">
                  <img
                    src={member.avatar}
                    alt={`演员 ${member.name}`}
                    title={`${member.name} 演员资料`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-sm text-foreground-950 group-hover:text-primary-700 transition-colors">
                    {member.name}
                  </h3>
                  <p className="text-xs text-foreground-500 line-clamp-1">{member.role}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {related.length > 0 && (
          <section className="mt-12 pb-16">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 flex items-center gap-3 mb-6">
              <span className="w-1.5 h-6 rounded-full bg-accent-500" />
              相关作品推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {related.map((item) => (
                <FilmCard key={item.id} film={item} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}